// Dubai - AC coil cleaning (chemical wash).
// Written for Dubai conditions; do not reuse text from the SG chemical wash
// page or the Abu Dhabi version (see the anti-cannibalisation rules in the
// expansion plan).
const content = {
    slug: 'ac-coil-chemical-wash',
    title: (c) => `AC Coil Cleaning Dubai from ${c.money(c.tables.chemicalWash[0].wall)} | Chemical Wash | StringsSG`,
    description: (c) =>
        `AC coil cleaning in Dubai: ${c.money(c.tables.chemicalWash[0].wall)} for a split unit, ${c.money(c.tables.chemicalWash[0].ducted)} for ducted. Clears dust, mould and smell from the coil and blower.`,
    eyebrow: 'Dubai · AC coil cleaning',
    h1: 'AC Coil Cleaning in Dubai',
    lead: (c) =>
        `When filter cleaning no longer brings the cold air back, the dirt is on the coil. A chemical wash strips the dust cake and mould off the indoor coil and blower wheel of your split or concealed ducted unit - ${c.money(c.tables.chemicalWash[0].wall)} for one split unit, travel included.`,
    image: 'blog/living/dirty-aircon-evaporator-coil.jpg',
    imageAlt: 'Dust-coated indoor AC coil before a chemical wash in a Dubai apartment',
    facts: ['Split and concealed ducted units', 'Price confirmed before the visit', 'Travel included'],
    priceTitle: 'Coil cleaning prices',
    priceIntro: (c) =>
        `Pick your unit type and how many units need cleaning. Ducted units cost more because the technician works through the ceiling access panel and cleans the plenum as well as the coil. Ducted from ${c.money(c.tables.chemicalWash[0].ducted)}.`,
    panel: { type: 'servicing', services: ['chemicalWash'], defaultMount: 'Ducted' },
    sections: [
        {
            title: 'Signs your coil needs a chemical wash',
            items: [
                'A damp, musty smell for the first few minutes after you switch the AC on.',
                'Black specks or grey fluff around the supply diffusers or on the louvres - that is mould and dust blown out of the unit.',
                'Air comes out cool but weak, even with a clean filter.',
                'The unit runs non-stop in the evening and the room still sits at 26°C or more.',
                'Water drips from the ceiling grille or the indoor unit after a long run.',
                'Your DEWA bill climbed this summer without any change in how you use the AC.',
            ],
            columns: 2,
        },
        {
            title: 'Why coils in Dubai clog faster',
            tone: 'muted',
            paragraphs: [
                'An AC in Dubai works far harder than one in a mild climate. From May to October most units run day and night, and even in the cooler months they rarely get a long rest. Every hour of running pulls room air - and whatever is in it - across the indoor coil.',
                'Fine desert dust is the main problem. During shamal winds and dust storms it gets into apartments through balcony doors, window seals and the fresh-air intake, and a lot of it passes straight through standard filters. On the cold, wet coil it sticks, mixes with condensate and bakes into a grey layer that blocks airflow. Mould grows on that layer because the coil is wet for most of the day.',
                'Concealed ducted units suffer most. They sit above the false ceiling, so nobody sees them, and the return-air filter behind the grille is the only part most tenants ever touch. By the time the smell starts, the coil has usually been dirty for a year or more.',
            ],
        },
        {
            // Target of the hero card's Service Details link
            id: 'included',
            title: 'What the chemical wash includes',
            items: [
                'Isolate the unit and protect the floor and furniture below it.',
                'Remove and wash the filter and return-air grille.',
                'Apply coil cleaner to both faces of the indoor coil and leave it to dissolve the dust and biofilm.',
                'Rinse the coil with low-pressure water and catch the run-off.',
                'Clean the blower wheel - the curved fan blades where mould builds up and causes the smell.',
                'Flush the drain tray and condensate line so the water you rinse out actually leaves the unit.',
                'Check airflow and supply-air temperature after reassembly, and show you the before-and-after.',
            ],
            columns: 2,
        },
        {
            title: 'Before the technician arrives',
            cards: [
                { title: 'Building access', body: 'Many Dubai towers need a contractor gate pass or work permit from building management. Tell us when you book and we send the technician and company details for the permit.' },
                { title: 'Ducted unit access', body: 'For a concealed unit we need the ceiling access panel. If it is painted shut or blocked by built-in wardrobes, let us know - the technician brings tools to open it cleanly.' },
                { title: 'District-cooled buildings', body: 'If your building is on district cooling, your apartment unit is a chilled-water fan coil. It still needs its coil and drain cleaned, but there is no refrigerant gas to refill.' },
            ],
        },
        {
            title: 'How often to chemical wash in Dubai',
            narrow: true,
            paragraphs: [
                'For most apartments, once a year is enough if the filters are cleaned every month or two and the unit gets a routine clean every three to four months. The best time is March or April, before the heat - your unit then starts summer with a clean coil instead of fighting dust from the winter.',
                'Homes near construction sites, ground-floor apartments and villas with pets, or anyone with allergies or asthma, usually need a coil wash every six months.',
            ],
        },
    ],
    faqs: [
        {
            question: 'How much does AC coil cleaning cost in Dubai?',
            answer: (c) => `With StringsSG, a chemical wash costs ${c.money(c.tables.chemicalWash[0].wall)} for one split unit and ${c.money(c.tables.chemicalWash[0].ducted)} for one concealed ducted unit. The price per unit drops as you add units - three split units cost ${c.money(c.tables.chemicalWash[2].wall)} in total. Travel is included.`,
        },
        {
            question: 'Is coil cleaning the same as duct cleaning?',
            answer: 'No. Coil cleaning cleans the indoor unit itself - the coil, blower wheel and drain tray. Duct cleaning cleans the ducts that carry air from a ducted unit to the rooms. If the smell or weak airflow comes from the unit, coil cleaning fixes it; dusty ducts are a separate, less frequent job.',
        },
        {
            question: 'How long does a coil chemical wash take?',
            answer: 'About 60 to 90 minutes for a split unit and two to three hours for a concealed ducted unit, depending on how dirty it is and how easy the access panel is to work through.',
        },
        {
            question: 'Will the chemicals harm my family or pets?',
            answer: 'The coil cleaner is rinsed off before the unit is reassembled, and the run-off is caught and taken away. Keep pets out of the room while the technician works and air the room for a few minutes afterwards.',
        },
        {
            question: 'My landlord says AC maintenance is my job - is coil cleaning included?',
            answer: 'Tenancy contracts in Dubai usually split AC upkeep: routine cleaning is often the tenant\'s job, while major repairs fall on the landlord. Check your contract and see our guide on who pays for AC maintenance in Dubai.',
        },
        {
            question: 'Do you clean units in district-cooled buildings?',
            answer: 'Yes. The fan coil in a district-cooled apartment gets the same coil, blower and drain clean. We do not touch the building\'s chilled-water supply - that belongs to the building and the cooling provider.',
        },
    ],
    schema: {
        name: 'AC Coil Cleaning (Chemical Wash) in Dubai',
        serviceType: 'Air conditioner coil chemical cleaning',
        offers: (c) => [
            { name: 'Chemical wash, 1 split unit', price: c.tables.chemicalWash[0].wall },
            { name: 'Chemical wash, 2 split units', price: c.tables.chemicalWash[1].wall },
            { name: 'Chemical wash, 3 split units', price: c.tables.chemicalWash[2].wall },
            { name: 'Chemical wash, 1 ducted unit', price: c.tables.chemicalWash[0].ducted },
            { name: 'Chemical wash, 2 ducted units', price: c.tables.chemicalWash[1].ducted },
        ],
    },
    closingCta: {
        title: 'Book before the summer rush',
        body: 'Coil cleaning slots fill up from April. Pick your units and a time slot - you see the price before you book.',
    },
}

export default content
